import { useQuery } from "react-query";

import axios from "axios";

import { RasterAttributes } from "models/model";
import { GeoTiffRenderingMode } from "models/projects";

export interface Shapefile {
  fileType?: string;
  layerName: string;
  lineStyle?: string;
  fillPattern?: string;
  opacity?: string;
  shapefileId: string;
  projectShapefileId?: string;
  rasterAttributes?: RasterAttributes;
  renderingMode?: GeoTiffRenderingMode;
  key?: string;
  source?: string;
  boundingBox?: number[];
  featureCollection: GeoJSON.FeatureCollection;
  features: Array<{
    color?: string;
    opacity?: number;
    shapefileGeomId: string;
    strokeColor?: string;
    thickness?: number;
    lineStyle?: string;
    fillPattern?: string;
    FID?: number;
  }>;
}

export const SHAPEFILE_LIST_QUERY_KEYS = {
  currentProjectShapefileList: "currentShapefileList"
};

export type UseShapefileListQueryKeysT = keyof typeof SHAPEFILE_LIST_QUERY_KEYS;

export interface useShapefilesListQueryParams {
  projectId: string;
  shapefileIds: string[];
  projectLinkedShapefileIds: string[];
  onSuccess?: (data) => void;
  onError?: (error) => void;
  queryKey?: UseShapefileListQueryKeysT;
}

export interface useShapefilesListQueryReturn {
  data: Shapefile[];
  error;
  isError: boolean;
  isIdle: boolean;
  isFetching: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  refetch: () => void;
}

const fetchShapefilePropertiesRequest = async ({ queryKey }) => {
  const [, projectId, shapefileIds, projectLinkedShapefileIds] = queryKey;
  const response = axios.post(`${process.env.REACT_APP_PROJECT_SERVICE}/shapefiles`, {
    projectId: projectId,
    shapefileIds: shapefileIds,
    projectShapefileIds: projectLinkedShapefileIds
  });

  return response;
};

/**
 * Fetches shapefile properties
 * @param params
 * @returns
 */
export function useShapefilesListQuery(
  params: useShapefilesListQueryParams
): useShapefilesListQueryReturn {
  const {
    onError,
    onSuccess,
    projectId,
    shapefileIds,
    queryKey,
    projectLinkedShapefileIds: projectLinkedShapefileIds
  } = params;

  const queryInfo = useQuery({
    queryKey: queryKey || [
      SHAPEFILE_LIST_QUERY_KEYS.currentProjectShapefileList,
      projectId,
      shapefileIds,
      projectLinkedShapefileIds
    ],
    cacheTime: 0, //do not cache
    queryFn: fetchShapefilePropertiesRequest,
    onError: (error) => {
      onError && onError(error);
    },
    onSuccess: (data) => {
      onSuccess && onSuccess(data);
    },
    // Enable automatic refreshing after projectId and shapefileIds have been defined
    enabled:
      !!params.projectId &&
      (!!params.shapefileIds?.length || !!params.projectLinkedShapefileIds?.length)
  });

  return {
    error: queryInfo.error,
    isError: queryInfo.status === "error",
    isIdle: queryInfo.status === "idle",
    isFetching: queryInfo.isFetching,
    isLoading: queryInfo.status === "loading",
    isSuccess: queryInfo.status === "success",
    refetch: queryInfo.refetch,
    data: queryInfo.data?.data
  };
}
